import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, tokenStore, type ScenarioOverview } from '../api-client';

export default function Overview() {
  const [scenarios, setScenarios] = useState<ScenarioOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tokenStore.get()) {
      window.location.href = '/login';
      return;
    }
    api
      .listScenarios()
      .then((r) => setScenarios(r.scenarios))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-slate-500">読み込み中…</p>;
  if (error) return <p className="text-red-600">エラー: {error}</p>;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">連携状況</h2>
      <p className="text-sm text-slate-500 mb-6">
        {new Date().getFullYear()}年{new Date().getMonth() + 1}月時点
      </p>

      {scenarios.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded p-5">
          <p className="text-amber-900">まだ連携シナリオがありません。</p>
          <p className="text-sm text-amber-700 mt-2">
            Craval にご相談いただくと、3〜10営業日でシナリオを構築します。
          </p>
          <Link
            to="/requests"
            className="mt-3 inline-block px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            新シナリオを依頼する
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {scenarios.map((s) => (
            <ScenarioCard key={s.id} scenario={s} />
          ))}
        </div>
      )}

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-5">
        <h3 className="font-semibold text-blue-900 mb-2">
          ℹ️ Cogneolink は SI型サービスです
        </h3>
        <p className="text-sm text-blue-800">
          設定変更や新しい連携の追加は、Craval にご依頼ください。
          通常 3〜10営業日で対応いたします。
        </p>
        <div className="mt-3 flex gap-2">
          <Link
            to="/requests"
            className="inline-block px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            設定変更を依頼する
          </Link>
          <Link
            to="/support"
            className="inline-block px-4 py-2 bg-white border border-blue-300 text-blue-700 text-sm rounded hover:bg-blue-50"
          >
            サポートに連絡
          </Link>
        </div>
      </div>
    </div>
  );
}

function ScenarioCard({ scenario: s }: { scenario: ScenarioOverview }) {
  const lastRunDate = s.lastRun
    ? new Date(s.lastRun.startedAt).toLocaleString('ja-JP')
    : 'まだ実行されていません';

  return (
    <div className="bg-white rounded-lg shadow border p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <StatusIcon status={s.lastRun?.status ?? 'pending'} />
            <h3 className="font-semibold text-lg">{s.name}</h3>
          </div>
          <p className="text-sm text-slate-500">最終実行: {lastRunDate}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">今月の処理</p>
          <p className="text-2xl font-bold">{s.thisMonth.total}件</p>
          <p className="text-xs">
            <span className="text-green-600">成功 {s.thisMonth.success}</span>
            {s.thisMonth.total - s.thisMonth.success > 0 && (
              <span className="text-red-600 ml-2">
                失敗 {s.thisMonth.total - s.thisMonth.success}
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'success') return <span className="text-green-600 text-lg">✓</span>;
  if (status === 'failed') return <span className="text-red-600 text-lg">✗</span>;
  return <span className="text-amber-600 text-lg">⏳</span>;
}
